/** @format */
import { Address, Environment, PersonalInformation, Phone } from '@shared/data-access/interfaces';
import { Identity, IdentityResponse, IdentityV2 } from '@my-account/data-access/interfaces';
import { HttpService, Logger, Store } from '@shared/data-access/services';

import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IdentityService {
	constructor(
		private readonly environment: Environment,
		private readonly httpService: HttpService,
		private readonly logger: Logger,
		private readonly store: Store
	) {}

	public getIdentity(includePhones = false, includeAddresses = false): Observable<Identity> {
		const manifest = [];

		if (includePhones) {
			manifest.push('IncludePhoneBook');
		}

		if (includeAddresses) {
			manifest.push('IncludeAddressBook');
		}

		const headers = {
			identityManifest: ''
		};

		if (manifest.length > 0) {
			headers.identityManifest = manifest.join(',');
		}

		const url = `${this.environment.baseUrls.identityService}/v1/identity`;

		return this.getWithDefaultHeaders(url, 0, {
			headers
		});
	}

	public getIdentityV2(): Observable<IdentityV2> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/identity`;

		return this.getWithDefaultHeaders(url, 0);
	}

	public getAddresses(): Observable<IdentityResponse<Address>> {
		const url = `${this.environment.baseUrls.identityService}/v1/address`;

		const headers = {
			addressmanifest: 'IncludeAll'
		};

		return this.getWithDefaultHeaders(url, 0, {
			headers
		}).pipe(
			map((result: IdentityResponse<Address>) => {
				this.store.select<PersonalInformation>('PERSONAL_INFO').subscribe((info) => {
					info.addresses = result.data;
					this.store.set('PERSONAL_INFO', info);
				});
				return result;
			})
		);
	}

	public createAddress(address: Address): Observable<string> {
		const url = `${this.environment.baseUrls.identityService}/v1/address`;

		return this.postWithDefaultHeaders(url, address);
	}

	public deleteAddress(id: string) {
		const url = `${this.environment.baseUrls.identityService}/v1/address/delete/${id}`;

		return this.postWithDefaultHeaders(url, {});
	}

	public getPhones(): Observable<IdentityResponse<Phone>> {
		const url = `${this.environment.baseUrls.identityService}/v1/phone`;

		return this.getWithDefaultHeaders(url).pipe(
			map((result: IdentityResponse<Phone>) => {
				this.store.select<PersonalInformation>('PERSONAL_INFO').subscribe((info) => {
					info.phoneNumbers[0] = result.data[0];
					this.store.set('PERSONAL_INFO', info);
				});
				return result;
			})
		);
	}

	public createPhone(phone: Phone): Observable<string> {
		const url = `${this.environment.baseUrls.identityService}/v1/phone`;

		return this.postWithDefaultHeaders(url, phone);
	}

	public deletePhone(id: string) {
		const url = `${this.environment.baseUrls.identityService}/v1/phone/delete/${id}`;

		return this.postWithDefaultHeaders(url, {});
	}

	private postWithDefaultHeaders<T>(
		url: string,
		input: object,
		httpOptions: object = {}
	): Observable<T> {
		httpOptions = this.setDefaultHeaders(httpOptions);

		return this.httpService.post(url, input, httpOptions);
	}

	private getWithDefaultHeaders<T>(
		url: string,
		retries = 0,
		httpOptions: object = {}
	): Observable<T> {
		httpOptions = this.setDefaultHeaders(httpOptions);

		return this.httpService.get(url, retries, httpOptions);
	}

	private setDefaultHeaders(httpOptions: object = {}): object {
		if (httpOptions['headers'] === undefined) {
			httpOptions['headers'] = {};
		}

		httpOptions['headers']['partner_key'] = this.environment.identityServiceKey.partnerKey;
		httpOptions['headers']['secret_key'] = this.environment.identityServiceKey.secretKey;

		this.logger.debug(httpOptions);

		return httpOptions;
	}
}
